import React, { useState, useEffect, useCallback } from 'react';
import { jobApi } from '../api/jobApi';
import { useLanguage } from '../context/LanguageContext';
import JobCard from '../components/JobCard';
import './HomePage.css';

const CATEGORIES = [
  { value: '', key: 'allCategories' },
  { value: 'RESTAURANT', key: 'restaurant' },
  { value: 'CAFE', key: 'cafe' },
  { value: 'STORE', key: 'store' },
  { value: 'FACTORY', key: 'factory' },
  { value: 'OFFICE', key: 'office' },
  { value: 'TEACHING', key: 'teaching' },
  { value: 'OTHER', key: 'other' },
];

function HomePage() {
  const { t } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [locating, setLocating] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState('LATEST');

  useEffect(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=ko`
          );
          const data = await res.json();
          const addr = data.address || {};
          const name = addr.city || addr.town || addr.county || addr.state || '';
          if (name) setLocation(name);
        } catch (e) {
          console.warn('Reverse geocoding failed:', e);
        } finally {
          setLocating(false);
        }
      },
      () => setLocating(false),
      { timeout: 5000, maximumAge: 300000 }
    );
  }, []);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        keyword: keyword || undefined,
        category: category || undefined,
        location: location || undefined,
        sortBy,
        page,
        size: 20
      };
      const hasSearch = keyword || category || location || sortBy !== 'LATEST';
      const res = hasSearch
        ? await jobApi.searchJobs(params)
        : await jobApi.getJobs(page);
      setJobs(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [keyword, category, location, sortBy, page]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleSearch = (e) => { e.preventDefault(); setPage(0); };

  return (
    <div className="home container">
      {/* Search */}
      <div className="search-section">
        <h1 className="site-title">OneJob</h1>
        <p className="site-desc">{t('searchPlaceholder')}</p>
        <form className="search-bar" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="search-input"
          />
          <input
            type="text"
            placeholder={locating ? t('locating') || '위치 찾는 중...' : t('location')}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className={`search-input search-loc${locating ? ' locating' : ''}`}
          />
          <button type="submit" className="btn btn-primary">{t('search')}</button>
        </form>
      </div>

      {/* Categories */}
      <div className="categories">
        {CATEGORIES.map(cat => (
          <button
            key={cat.value}
            className={`cat-btn ${category === cat.value ? 'active' : ''}`}
            onClick={() => { setCategory(cat.value); setPage(0); }}
          >
            {t(cat.key)}
          </button>
        ))}
        <span className="sort-sep" />
        {[
          { value: 'LATEST', key: 'sortLatest' },
          { value: 'HIGHEST_WAGE', key: 'sortWage' },
          { value: 'POPULAR', key: 'sortPopular' },
        ].map(opt => (
          <button
            key={opt.value}
            className={`sort-btn ${sortBy === opt.value ? 'active' : ''}`}
            onClick={() => { setSortBy(opt.value); setPage(0); }}
          >
            {t(opt.key)}
          </button>
        ))}
      </div>

      {/* Job List */}
      {loading ? (
        <div className="loading-grid">
          {[...Array(6)].map((_, i) => <div key={i} className="skeleton" />)}
        </div>
      ) : jobs.length === 0 ? (
        <div className="empty">{t('noResults')}</div>
      ) : (
        <>
          <div className="job-list">
            {jobs.map(job => <JobCard key={job.id} job={job} />)}
          </div>
          {totalPages > 1 && (
            <div className="pages">
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>&lsaquo;</button>
              {[...Array(Math.min(totalPages, 7))].map((_, i) => {
                const start = Math.max(0, Math.min(page - 3, totalPages - 7));
                const num = start + i;
                if (num >= totalPages) return null;
                return (
                  <button key={num} className={num === page ? 'current' : ''} onClick={() => setPage(num)}>
                    {num + 1}
                  </button>
                );
              })}
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>&rsaquo;</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HomePage;
